import React, { useEffect, useState } from "react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import Avatar from "components/ui/Avatar";
import LocalDateTime from "services/date-converter";
import sortData from "utils/sorting-coloum";
import { useAlert } from "context/AlertContext";
import {
  Eye,
  CheckCircle2,
  XCircle,
  Trash2,
  Calendar,
  Wallet,
  CalendarClock,
  ShoppingCart,
  Flag,
  FileCheck2,
  ShieldCheck,
  ArrowLeft,
} from "lucide-react";
import apiApproval from "./api";
import ApprovalStats from "./approval-stats/ApprovalStats";
import ApprovalDetail from "./approval-detail/ApprovalDetail";
import ApprovalModuleCards from "./approval-modules/ApprovalModuleCards";
import { MODULE_TYPES } from "./approval-modules/module-config";

const TYPE_ICON = {
  LEAVE_REQUEST: CalendarClock,
  EXPENSE_CLAIM: Wallet,
  PURCHASE_ORDER: ShoppingCart,
  PROJECT_MILESTONE: Flag,
};

const PRIORITY_STYLES = {
  HIGH: "bg-high_bg text-high border-high_border",
  MEDIUM: "bg-warning_bg text-warning border-warning_border",
  LOW: "bg-info_bg text-info border-info_border",
};

const PRIORITY_DOT = {
  HIGH: "bg-high",
  MEDIUM: "bg-warning",
  LOW: "bg-info",
};

const STATUS_STYLES = {
  PENDING: "bg-warning_bg text-warning border-warning_border",
  APPROVED: "bg-success_bg text-success border-success_border",
  REJECTED: "bg-danger_bg text-danger border-danger_border",
};

const STATUS_DOT = {
  PENDING: "bg-warning",
  APPROVED: "bg-success",
  REJECTED: "bg-danger",
};

const SORT_OPTIONS = [
  { value: "requested_date_desc", label: "Newest first" },
  { value: "requested_date_asc", label: "Oldest first" },
];

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const ApprovalNotifications = () => {
  const { showAlert } = useAlert();

  const [selectedModule, setSelectedModule] = useState(null);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortConfig, setSortConfig] = useState({ key: "requested_date", direction: "desc" });
  const [sortValue, setSortValue] = useState("requested_date_desc");
  const [resetSelectionTrigger, setResetSelectionTrigger] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [stats, setStats] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0, ALL: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [confirmData, setConfirmData] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Yes",
    cancelText: "No",
    icon: null,
    resolve: null,
  });

  const fetchDatas = async () => {
    if (!selectedModule) return;
    setLoading(true);
    try {
      const payload = {
        searchText,
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        type: selectedModule,
        ...(statusFilter !== "ALL" && { status: statusFilter }),
      };

      const response = await apiApproval.getAll(payload);

      if (response.data.status) {
        setData(response.data.data);
        setTotalCount(response.data.total_count || 0);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "An unexpected error occurred. Please try again!",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!selectedModule) return;
    setStatsLoading(true);
    try {
      const statusKeys = ["ALL", "PENDING", "APPROVED", "REJECTED"];
      const results = await Promise.allSettled(
        statusKeys.map((key) =>
          apiApproval.getAll({
            type: selectedModule,
            skip: 0,
            limit: 1,
            ...(key !== "ALL" && { status: key }),
          })
        )
      );

      const nextStats = { PENDING: 0, APPROVED: 0, REJECTED: 0, ALL: 0 };
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          nextStats[statusKeys[index]] = result.value.data?.total_count || 0;
        }
      });
      setStats(nextStats);
    } catch (error) {
      // Non-critical — leave stats at last known value.
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchDatas();
  }, [currentPage, pageSize, searchText, statusFilter, selectedModule]);

  useEffect(() => {
    if (!selectedModule) return;
    fetchStats();
  }, [selectedModule]);

  useEffect(() => {
    const sorted = sortData(data, sortConfig);
    setFilteredData(sorted);
  }, [data, sortConfig]);

  const openConfirm = (options) => {
    return new Promise((resolve) => {
      setConfirmData({ ...options, isOpen: true, resolve });
    });
  };

  const handleConfirmClose = (result) => {
    if (confirmData.resolve) confirmData.resolve(result);
    setConfirmData((prev) => ({ ...prev, isOpen: false, resolve: null }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page, newPageSize = pageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSortChange = (value) => {
    setSortValue(value);
    setSortConfig({ key: "requested_date", direction: value.endsWith("_desc") ? "desc" : "asc" });
  };

  const handleRefresh = () => {
    setSearchText("");
    setCurrentPage(1);
    setResetTrigger((prev) => prev + 1);
    fetchDatas();
    fetchStats();
  };

  const handleSelectModule = (moduleKey) => {
    setSelectedModule(moduleKey);
    setStatusFilter("ALL");
    setSearchText("");
    setCurrentPage(1);
    setSelectedRows([]);
  };

  const handleBackToModules = () => {
    setSelectedModule(null);
    setData([]);
    setFilteredData([]);
    setTotalCount(0);
    setSelectedRows([]);
  };

  const handleView = (row) => {
    setSelectedRequest(row);
    setDetailOpen(true);
  };

  const handleApprove = async (row, remarks) => {
    const confirmed = await openConfirm({
      title: "Approve Request",
      message: `Are you sure you want to approve "${row.title}"?`,
      confirmText: "Approve",
      cancelText: "Cancel",
      icon: <CheckCircle2 size={24} className="text-success" />,
      variant: "primary",
    });
    if (!confirmed) return;

    setActionLoading(true);
    try {
      const response = await apiApproval.approve(row.id, { remarks });
      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message || "Request approved successfully" });
        setDetailOpen(false);
        fetchDatas();
        fetchStats();
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "Something went wrong",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (row, remarks) => {
    const confirmed = await openConfirm({
      title: "Reject Request",
      message: `Are you sure you want to reject "${row.title}"?`,
      confirmText: "Reject",
      cancelText: "Cancel",
      icon: <XCircle size={24} className="text-danger" />,
    });
    if (!confirmed) return;

    setActionLoading(true);
    try {
      const response = await apiApproval.reject(row.id, { remarks });
      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message || "Request rejected" });
        setDetailOpen(false);
        fetchDatas();
        fetchStats();
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "Something went wrong",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSelected = async (ids = []) => {
    if (ids.length === 0) {
      showAlert({ type: "warning", message: "Please select at least one notification to dismiss." });
      return;
    }
    const confirmed = await openConfirm({
      title: "Dismiss Notifications",
      message:
        ids.length > 1
          ? `Are you sure you want to dismiss ${ids.length} selected notifications? This action cannot be undone.`
          : "Are you sure you want to dismiss this notification? This action cannot be undone.",
      confirmText: "Dismiss",
      cancelText: "Cancel",
      icon: <Trash2 size={24} className="text-danger" />,
    });
    if (!confirmed) return;

    try {
      const response = await apiApproval.delete(ids);
      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message || "Dismissed successfully" });
        fetchDatas();
        fetchStats();
        setSelectedRows([]);
        setResetSelectionTrigger((prev) => prev + 1);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "An unexpected error occurred. Please try again!",
      });
    }
  };

  const handleBulkApprove = async (count) => {
    const confirmed = await openConfirm({
      title: "Approve Requests",
      message: `Are you sure you want to approve ${count} selected request${count > 1 ? "s" : ""}?`,
      confirmText: "Approve",
      cancelText: "Cancel",
      icon: <CheckCircle2 size={24} className="text-success" />,
      variant: "primary",
    });
    if (!confirmed) return;

    try {
      const response = await apiApproval.bulkApprove(selectedRows);
      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message || `${count} request(s) approved` });
        fetchDatas();
        fetchStats();
        setSelectedRows([]);
        setResetSelectionTrigger((prev) => prev + 1);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "Something went wrong",
      });
    }
  };

  const handleBulkReject = async (count) => {
    const confirmed = await openConfirm({
      title: "Reject Requests",
      message: `Are you sure you want to reject ${count} selected request${count > 1 ? "s" : ""}?`,
      confirmText: "Reject",
      cancelText: "Cancel",
      icon: <XCircle size={24} className="text-danger" />,
    });
    if (!confirmed) return;

    try {
      const response = await apiApproval.bulkReject(selectedRows);
      if (response.data.status) {
        showAlert({ type: "success", message: response.data?.message || `${count} request(s) rejected` });
        fetchDatas();
        fetchStats();
        setSelectedRows([]);
        setResetSelectionTrigger((prev) => prev + 1);
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "Something went wrong",
      });
    }
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
    setResetSelectionTrigger((prev) => prev + 1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const payload = {
        searchText,
        skip: 0,
        limit: totalCount || 1000,
        type: selectedModule,
        ...(statusFilter !== "ALL" && { status: statusFilter }),
      };
      const response = await apiApproval.getAll(payload);
      const rows = response.data?.data || [];

      if (rows.length === 0) {
        showAlert({ type: "warning", message: "There is no data to export." });
        return;
      }

      const header = ["Title", "Type", "Requested By", "Email", "Priority", "Status", "Requested On", "Due Date"];
      const lines = rows.map((row) =>
        [
          row.title,
          row.type_label || row.type,
          `${row.requested_by?.first_name || ""} ${row.requested_by?.last_name || ""}`.trim(),
          row.requested_by?.email,
          row.priority,
          row.status,
          row.requested_date,
          row.due_date,
        ]
          .map(csvCell)
          .join(",")
      );

      const csv = [header.map(csvCell).join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `approval-notifications-${statusFilter.toLowerCase()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to export data",
      });
    } finally {
      setExporting(false);
    }
  };

  const bulkActions = [
    { key: "approve", label: "Approve", icon: CheckCircle2, variant: "success", onClick: handleBulkApprove },
    { key: "reject", label: "Reject", icon: XCircle, variant: "danger", onClick: handleBulkReject },
    { key: "dismiss", label: "Dismiss", icon: Trash2, variant: "default", onClick: () => handleDeleteSelected(selectedRows) },
  ];

  const columns = [
    {
      key: "title",
      label: "Request",
      type: "string",
      width: "280px",
      sortable: true,
      render: (row) => {
        const Icon = TYPE_ICON[row.type] || FileCheck2;
        return (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface_alt">
              <Icon size={16} className="text-text_muted" />
            </div>
            <div className="min-w-0">
              <div className="truncate font-medium text-text_primary">{row.title}</div>
              <div className="truncate text-xs text-text_muted">{row.type_label || row.type}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: "requested_by",
      label: "Requested By",
      type: "string",
      width: "220px",
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2.5">
          <div className="flex-shrink-0">
            <Avatar user={row.requested_by} size={30} />
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-text_primary">
              {row.requested_by?.first_name} {row.requested_by?.last_name}
            </div>
            <div className="truncate text-xs text-text_muted">{row.requested_by?.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "priority",
      label: "Priority",
      type: "string",
      width: "110px",
      sortable: true,
      render: (row) => (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${
            PRIORITY_STYLES[row.priority] || PRIORITY_STYLES.LOW
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[row.priority] || PRIORITY_DOT.LOW}`} />
          {row.priority || "LOW"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      type: "string",
      width: "120px",
      sortable: true,
      render: (row) => (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${
            STATUS_STYLES[row.status] || STATUS_STYLES.PENDING
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[row.status] || STATUS_DOT.PENDING}`} />
          {row.status}
        </span>
      ),
    },
    {
      key: "requested_date",
      label: "Requested On",
      width: "150px",
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-1.5 text-xs text-text_primary">
          <Calendar size={12} className="text-text_muted" />
          <LocalDateTime utcTime={row.requested_date} type="datetime" />
        </div>
      ),
    },
  ];

  const renderRowActions = (row) => (
    <div className="flex items-center gap-1.5">
      <button
        className="p-1.5 rounded-lg border border-info_border bg-info_bg text-info"
        onClick={(e) => {
          e.stopPropagation();
          handleView(row);
        }}
        title="View Details"
      >
        <Eye size={15} />
      </button>

      {row.status === "PENDING" && (
        <>
          <button
            className="p-1.5 rounded-lg border border-success_border bg-success_bg text-success"
            onClick={(e) => {
              e.stopPropagation();
              handleApprove(row, "");
            }}
            title="Approve"
          >
            <CheckCircle2 size={15} />
          </button>
          <button
            className="p-1.5 rounded-lg border border-danger_border bg-danger_bg text-danger"
            onClick={(e) => {
              e.stopPropagation();
              handleReject(row, "");
            }}
            title="Reject"
          >
            <XCircle size={15} />
          </button>
        </>
      )}

      <button
        className="p-1.5 rounded-lg border border-border text-text_muted"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteSelected([row.id]);
        }}
        title="Dismiss"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );

  const activeModule = MODULE_TYPES.find((module) => module.key === selectedModule);

  return (
    <div className="bg-background">
      <div className="max-w-[100rem] mx-auto overflow-y-auto" style={{ height: "calc(100vh - 70px)" }}>
        <div className="min-h-full bg-surface border border-border rounded-sm">
          <div className="p-4 space-y-5">

            {/* Header */}
            <div className="flex items-center gap-3">
              {selectedModule && (
                <button
                  type="button"
                  onClick={handleBackToModules}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-text_muted cursor-pointer"
                  title="Back to modules"
                >
                  <ArrowLeft size={16} />
                </button>
              )}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary_tint bg-primary_tint text-primary">
                <ShieldCheck size={20} />
              </div>
              <div className="min-w-0">
                <h1 className="text-[17px] font-semibold tracking-tight text-text_primary">
                  {activeModule ? activeModule.label : "Approval Notifications"}
                </h1>
                <p className="text-xs text-text_secondary">
                  {activeModule
                    ? activeModule.description
                    : "Select a module to review, approve, or reject requests raised across your organization."}
                </p>
              </div>
            </div>

            {!selectedModule ? (
              <ApprovalModuleCards onSelect={handleSelectModule} />
            ) : (
              <>
                <ApprovalStats
                  stats={stats}
                  loading={statsLoading}
                  activeFilter={statusFilter}
                  onSelect={(key) => {
                    setStatusFilter(key);
                    setCurrentPage(1);
                  }}
                />

                <TableToolBar
                  onSearch={handleSearch}
                  onRefresh={handleRefresh}
                  selectedCount={selectedRows.length}
                  resetSearchTrigger={resetTrigger}
                  canCreate={false}
                  resultCount={totalCount}
                  sortOptions={SORT_OPTIONS}
                  sortValue={sortValue}
                  onSortChange={handleSortChange}
                  bulkActions={bulkActions}
                  onClearSelection={handleClearSelection}
                  onExport={handleExport}
                  exporting={exporting}
                />

                {data?.length === 0 && !loading ? (
                  <div className="flex items-center justify-center rounded-xl border border-dashed border-border bg-surface_alt">
                    <NoDataState
                      icon="alert"
                      title="No Approval Requests Found"
                      message="There are no approval notifications to show right now."
                      showRefresh={true}
                      onRefresh={handleRefresh}
                      showBack={false}
                      showCreate={false}
                      size="medium"
                    />
                  </div>
                ) : (
                  <>
                    <div className="overflow-hidden border rounded-lg border-border">
                      <TableGrid
                        data={filteredData}
                        totalCount={totalCount}
                        loading={loading}
                        currentPage={currentPage}
                        columns={columns}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        onSelectionChange={setSelectedRows}
                        pageSize={pageSize}
                        selectable={true}
                        renderActions={renderRowActions}
                        resetSelectionTrigger={resetSelectionTrigger}
                      />
                    </div>

                    <Pagination
                      totalCount={totalCount}
                      currentPage={currentPage}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      selectedCount={selectedRows.length}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {confirmData.isOpen && (
        <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
      )}

      <ApprovalDetail
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        request={selectedRequest}
        onApprove={handleApprove}
        onReject={handleReject}
        actionLoading={actionLoading}
      />
    </div>
  );
};

export default ApprovalNotifications;
